// Normalización de cursos del banner/agenda (arregla etiquetas truncadas y anclas rotas guardadas en BD)
const MONTH_RE = /\s*·?\s*(enero|febrero|marzo|abril|mayo|junio|julio|agosto|septiembre|octubre|noviembre|diciembre)\s*$/i;

// ── Precios de los cursos de puntos (fuente única de verdad) ──
// Solo el número: los helpers de formato viven aquí para que ningún componente
// tenga que reescribir "215 €", "215 euros" o "215" por su cuenta. Cambiar el
// importe se hace en UNA sola línea. También expuesto en el backend
// (backend/server.py) como PRECIO_PUNTOS / PRECIO_CARNET.
export const PRECIO_PUNTOS = 215;
export const PRECIO_CARNET = 405;

// Helper genérico. Usa NBSP entre número y símbolo NUNCA — mantenemos el
// espacio normal para preservar el HTML byte-a-byte con lo que ya se servía.
export const formatEuro = (n) => `${n} €`;
export const formatEuros = (n) => `${n} euros`;

// Cadenas pre-formateadas, listas para inyectar en JSX / string templates.
export const PUNTOS_EUR = formatEuro(PRECIO_PUNTOS); // "215 €"
export const CARNET_EUR = formatEuro(PRECIO_CARNET); // "405 €"
export const PUNTOS_EUROS = formatEuros(PRECIO_PUNTOS); // "215 euros"
export const CARNET_EUROS = formatEuros(PRECIO_CARNET); // "405 euros"

// Precio como cadena sin unidad (usado por Schema.org Course/Offer).
export const PUNTOS_PRICE = String(PRECIO_PUNTOS); // "215"
export const CARNET_PRICE = String(PRECIO_CARNET); // "405"

// Etiquetas por defecto (usadas si no hay override configurable en /api/config)
export const DEFAULT_POINTS_LABEL = "Curso parcial (1 día + 3 h online · Tipo A)";
export const DEFAULT_CARNET_LABEL = "Curso total 20 h (Tipos B1 / B2 / B3)";
export const PUNTOS_DETAIL = `1 día presencial (viernes 15:00–22:30) + 3 h online flexibles · Plaza América · ${PUNTOS_EUR}`;
export const CARNET_DETAIL = `3 días presenciales + 4 h online · Plaza América · ${CARNET_EUR}`;

export function fixCtaHref(href) {
  if (!href) return href;
  if (href === "#puntos-formulario") return "/recuperacion-de-puntos-vigo#puntos-formulario";
  // Rewrites old /puntos URLs stored in the admin banner config
  if (href === "/puntos" || href.startsWith("/puntos#")) {
    return href.replace("/puntos", "/recuperacion-de-puntos-vigo");
  }
  if (href.startsWith("#")) return `/${href}`;
  return href;
}

export function cleanCourse(c, labels = null) {
  let label = (c.course_label || "")
    .replace(/recuper\.?\s+de\s+puntos/i, "Curso de recuperación de puntos (10 h)")
    .replace(/recuper\.?\s+del\s+carnet/i, "Curso de recuperación del carnet (20 h)")
    .replace(MONTH_RE, "")
    .replace(/\s{2,}/g, " ")
    .trim();
  const isPuntos = /punto/i.test(label) && !/carnet/i.test(label);
  const isCarnet = /carnet/i.test(label);
  let subtitle = (c.subtitle || "").replace(/\b1 semanas\b/i, "1 semana").replace(/\s{2,}/g, " ").trim();
  // Si viene un override de duración por tipo desde /api/config, prevalece
  // sobre PUNTOS_DETAIL/CARNET_DETAIL por defecto (siempre que el banner no
  // haya guardado un subtitle específico introducido a mano por el admin).
  const overrideA = labels && typeof labels.A === "string" && labels.A.trim();
  const overrideB1 = labels && typeof labels.B1 === "string" && labels.B1.trim();
  if (!subtitle || MONTH_RE.test(` · ${subtitle}`)) {
    if (isPuntos) subtitle = overrideA || PUNTOS_DETAIL;
    else if (isCarnet) subtitle = overrideB1 || CARNET_DETAIL;
    else subtitle = "";
  }
  return { ...c, course_label: label, subtitle, cta_href: fixCtaHref(c.cta_href) };
}

// Genera convocatorias futuras a partir del calendario fijo de cursos de puntos (A = parcial, B1 = total)
export function coursesFromSchedule(schedule, labels = null) {
  if (!schedule) return [];
  const mk = (arr, label, subtitle) =>
    (arr || []).map((s) => ({
      course_label: label,
      subtitle,
      start_date: `${s.start}T15:00:00`,
      cta_href: "/recuperacion-de-puntos-vigo#puntos-formulario",
      cta_label: "Reservar mi plaza",
      generated: true,
    }));
  const subA = (labels && typeof labels.A === "string" && labels.A.trim()) || PUNTOS_DETAIL;
  const subB1 = (labels && typeof labels.B1 === "string" && labels.B1.trim()) || CARNET_DETAIL;
  return [
    ...mk(schedule.A, "Curso de recuperación de puntos (10 h)", subA),
    ...mk(schedule.B1, "Curso de recuperación del carnet (20 h)", subB1),
  ];
}

// Combina cursos del admin (prioritarios: tienen plazas) con los generados del calendario, sin duplicar fecha+tipo
export function mergeCourses(adminCourses, generated) {
  const key = (c) => {
    const type = /carnet/i.test(c.course_label) ? "carnet" : /punto/i.test(c.course_label) ? "puntos" : c.course_label;
    return `${type}|${String(c.start_date).slice(0, 10)}`;
  };
  const seen = new Set(adminCourses.map(key));
  return [...adminCourses, ...generated.filter((g) => !seen.has(key(g)))];
}

// Cursos de la familia "permiso por puntos" (recuperación de puntos/carnet, sensibilización, ciclo)
export function isPointsCourse(c) {
  return /punto|carnet|sensibilizaci|ciclo adicional/i.test(c?.course_label || "");
}

// Etiqueta de plazas restantes (compartida por el banner y la agenda de cursos)
// Directiva Omnibus: el nº es un dato real editado manualmente en /admin.
// > 5 plazas: sin mensaje de urgencia. 0: completo, remitir a la siguiente convocatoria.
export function seatsLabel(n) {
  if (n === 0) return "Completo — consulta la siguiente convocatoria";
  if (typeof n === "number" && n <= 5) return `¡Últimas plazas! Quedan ${n}`;
  return null;
}
